import React from "react";
import TimezoneSelector from "./TimezoneSelector";
import { formatHourForDisplay } from "../../utils/timezoneUtils";
import "../../styles/preferencesForm.css";

// Define supported programming languages
const CODING_LANGUAGES = [
  { value: "python", label: "Python" },
  { value: "cpp", label: "C++" },
  { value: "java", label: "Java" },
  { value: "javascript", label: "JavaScript" }
];

/**
 * Shared preferences form component used by both PreferencesPopup and UserSettings
 * @param {Object} props - Component props
 * @param {number} props.frequencyDay - Email frequency in days
 * @param {function} props.setFrequencyDay - Function to update frequency
 * @param {string} props.userTimezone - User's timezone
 * @param {function} props.setUserTimezone - Function to update timezone
 * @param {number} props.preferredHour - Preferred hour for emails
 * @param {function} props.setPreferredHour - Function to update preferred hour
 * @param {string} props.codingLanguage - Preferred coding language
 * @param {function} props.setCodingLanguage - Function to update coding language
 * @param {string} props.variant - Component variant ('popup' or 'settings')
 */
const PreferencesForm = ({
  frequencyDay,
  setFrequencyDay,
  userTimezone,
  setUserTimezone,
  preferredHour,
  setPreferredHour,
  codingLanguage,
  setCodingLanguage,
  variant = "settings"
}) => {
  const isPopup = variant === "popup";
  const itemClass = isPopup ? "preference-item" : "setting-item";

  return (
    <>
      <div className={itemClass}>
        <label htmlFor={`frequencyDay-${variant}`}>
          {isPopup && <i className="bi bi-calendar-check"></i>} Email Frequency
        </label>
        {!isPopup && (
          <div className="setting-description">
            How often would you like to receive PrepLetter emails?
          </div>
        )}
        <select
          id={`frequencyDay-${variant}`}
          value={frequencyDay}
          onChange={(e) => setFrequencyDay(parseInt(e.target.value))}
        >
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <option key={day} value={day}>
              {day === 1 ? "Daily" : `Every ${day} days`}
            </option>
          ))}
        </select>
      </div>

      <TimezoneSelector
        id={`timezone-${variant}`}
        value={userTimezone}
        onChange={(e) => setUserTimezone(e.target.value)}
        label="Your Timezone"
        description={
          isPopup
            ? "Select your timezone to receive emails at a convenient time."
            : "Select your timezone to receive emails at a convenient time for you."
        }
      />

      <div className={itemClass}>
        <label htmlFor={`preferredHour-${variant}`}>
          {isPopup && <i className="bi bi-clock"></i>} 
          {isPopup ? "Preferred time to receive emails" : "Preferred Time"}
        </label>
        {!isPopup && (
          <div className="setting-description">
            What time of day would you like to receive your emails?
          </div>
        )}
        <select
          id={`preferredHour-${variant}`}
          value={preferredHour}
          onChange={(e) => setPreferredHour(parseInt(e.target.value))}
        >
          {Array.from({ length: 24 }, (_, i) => (
            <option key={i} value={i}>
              {formatHourForDisplay(i)}
            </option>
          ))}
        </select>
      </div>

      <div className={itemClass}>
        <label htmlFor={`codingLanguage-${variant}`}>
          {isPopup && <i className="bi bi-code-slash"></i>} 
          {isPopup ? "Preferred coding language" : "Preferred Language"}
        </label>
        {!isPopup && (
          <div className="setting-description">
            Which programming language would you like to use in your PrepLetters?
          </div>
        )}
        <select
          id={`codingLanguage-${variant}`}
          value={codingLanguage}
          onChange={(e) => setCodingLanguage(e.target.value)}
        >
          {CODING_LANGUAGES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default PreferencesForm;
